import {
  createLocalTrackF,
  hideVirtualBackgroundDialog,
  MEDIA_TYPE,
  useBaseStore,
  useShouldMirrorLocalVideo,
  useTrackMuted,
  useTranslation,
} from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import {
  useVirtualBackgroundStore,
  type BackgroundOption,
} from '../../virtual-background/state';
import Blur from '../icon/Blur';
import Icon from '../icon/Icon.web';
import SlightlyBlur from '../icon/SlightlyBlur';
import Video from '../video/Video.web';
import './VirtualBackground.web.css';

interface VirtualBackgroundTileProps {
  option: BackgroundOption;
  onSelect: (id: string) => void;
  selected: boolean;
}

const VirtualBackgroundTile = ({
  option,
  onSelect,
  selected,
}: VirtualBackgroundTileProps) => {
  const { t } = useTranslation();

  const handleAddBackground = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const newBackground = {
          id: `custom-${Date.now()}`,
          type: 'image' as const,
          src: URL.createObjectURL(file),
          label: file.name,
          selected: false,
        };

        useVirtualBackgroundStore.setState(state => ({
          backgrounds: [...state.backgrounds, newBackground],
        }));
      }
    };
    input.click();
  }, []);

  if (option.type === 'add') {
    return (
      <div className="cometchat-calls-virtual-background-option-wrapper">
        <button
          className="cometchat-calls-virtual-background-option cometchat-calls-virtual-background-option--add"
          onClick={handleAddBackground}
          aria-label={t('virtualBackground.addBackground')}
          title={option.label}
          tabIndex={0}>
          <Icon
            name="add"
            size={20}
            fill="var(--cometchat-calls-icon-color-primary)"
          />
        </button>
      </div>
    );
  }

  return (
    <div className="cometchat-calls-virtual-background-option-wrapper">
      {selected && (
        <div className="cometchat-calls-virtual-background-option-checkbox">
          <Icon
            name="check-fill"
            size={16}
            fill="var(--cometchat-calls-icon-color-primary)"
          />
        </div>
      )}

      <button
        key={option.id}
        className={clsx('cometchat-calls-virtual-background-option', {
          'cometchat-calls-virtual-background-option--selected': selected,
        })}
        onClick={() => onSelect(option.id)}
        aria-label={`${option.label} background${selected ? ', selected' : ''}`}
        aria-pressed={selected}
        title={option.label}>
        {option.type === 'none' && (
          <div className="cometchat-calls-virtual-background-option-none">
            <Icon
              name="remove"
              size={24}
              fill="var(--cometchat-calls-icon-color-secondary)"
            />
          </div>
        )}
        {option.type === 'blur' && (
          <div className="cometchat-calls-virtual-background-option-blur">
            {option.id == 'blur' ? <Blur /> : <SlightlyBlur />}
          </div>
        )}

        {option.type === 'image' && (
          <img
            className="cometchat-calls-virtual-background-option-image"
            crossOrigin="anonymous"
            src={option.src}
            alt={option.label ?? ''}
          />
        )}
        {/* Keep the original selected indicator inside the tile as well, or remove it if you only want the one above */}
        {selected && (
          <div className="cometchat-calls-virtual-background-option-selected-indicator"></div>
        )}
      </button>
    </div>
  );
};

const VirtualBackground = () => {
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [isDirty, setIsDirty] = useState(false);
  const { t } = useTranslation();

  const backgrounds = useVirtualBackgroundStore(state => state.backgrounds);
  const selectedBackgroundId = useVirtualBackgroundStore(
    state => state.selectedBackgroundId,
  );

  useEffect(() => {
    const currentSelection = backgrounds.find(b => b.selected)?.id;
    setIsDirty(currentSelection !== selectedBackgroundId);
  }, [backgrounds, selectedBackgroundId]);

  // Separate control options from image options
  const controlOptions = backgrounds.filter(option => option.type !== 'image');
  const imageOptions = backgrounds.filter(option => option.type === 'image');

  const videoTrack = useVirtualBackgroundStore(state => state.previewTrack);
  const videoMuted = useTrackMuted(MEDIA_TYPE.VIDEO);
  const shouldMirror = useShouldMirrorLocalVideo();

  const virtualBackgroundDialogVisible = useBaseStore(
    state => state.virtualBackgroundDialogVisible,
  );

  useEffect(() => {
    useVirtualBackgroundStore.getState().selectBackground(selectedBackgroundId);
  }, [selectedBackgroundId, virtualBackgroundDialogVisible]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (virtualBackgroundDialogVisible) {
      createLocalTrackF('video')
        .then(track => {
          useVirtualBackgroundStore.setState({ previewTrack: track });
        })
        .catch(error => {
          console.error('Error creating local video track:', error);
        });
    }
    return () => {
      useVirtualBackgroundStore.setState({ previewTrack: undefined });
    };
  }, [virtualBackgroundDialogVisible]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && virtualBackgroundDialogVisible) {
        hideVirtualBackgroundDialog();
      }
    };

    if (virtualBackgroundDialogVisible) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [virtualBackgroundDialogVisible]);

  const handleBackgroundSelect = useCallback((backgroundId: string) => {
    useVirtualBackgroundStore.getState().selectBackground(backgroundId);
  }, []);

  const handleDone = useCallback(() => {
    const selectedBackgroundId = backgrounds.find(b => b.selected)?.id;
    useVirtualBackgroundStore.setState({
      selectedBackgroundId: selectedBackgroundId,
    });
    useVirtualBackgroundStore.getState().promotePreviewVirtualBackground();
    hideVirtualBackgroundDialog();
  }, [backgrounds]);

  // Video preview render - copied exactly from Settings
  const renderVideoPreview = () => {
    return (
      <div className="cometchat-calls-virtual-background-preview-video-container">
        <Video
          videoTrack={videoTrack}
          className={clsx(
            'cometchat-calls-virtual-background-preview-video',
            shouldMirror && 'cometchat-calls-video--mirrored',
          )}
        />
        {videoMuted && (
          <div className="cometchat-calls-virtual-background-preview-placeholder">
            <Icon name="video-off" size={48} aria-hidden="true" />
            <span className="cometchat-calls-virtual-background-preview-placeholder-text">
              {t('virtualBackground.noCameraFeed')}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderBackgroundGrid = () => (
    <div className="cometchat-calls-virtual-background-options">
      <div className="cometchat-calls-virtual-background-controls-row">
        {controlOptions.map(option => (
          <VirtualBackgroundTile
            key={option.id}
            option={option}
            selected={option.selected}
            onSelect={handleBackgroundSelect}
          />
        ))}
      </div>

      <div className="cometchat-calls-virtual-background-images-row">
        {imageOptions.map(option => (
          <VirtualBackgroundTile
            key={option.id}
            option={option}
            selected={option.selected}
            onSelect={handleBackgroundSelect}
          />
        ))}
      </div>
    </div>
  );

  if (!virtualBackgroundDialogVisible) {
    return null;
  }

  return (
    <div
      className="cometchat-calls-virtual-background-overlay"
      onClick={hideVirtualBackgroundDialog}
      role="dialog"
      aria-modal="true"
      aria-labelledby="virtual-background-modal-title">
      <div
        className={`cometchat-calls-virtual-background-modal ${isMobileView ? 'cometchat-calls-virtual-background-modal--mobile' : 'cometchat-calls-virtual-background-modal--desktop'}`}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cometchat-calls-virtual-background-header">
          <h2
            className="cometchat-calls-virtual-background-header-title"
            id="virtual-background-modal-title">
            {t('virtualBackground.title')}
          </h2>
        </div>

        {/* Content */}
        <div className="cometchat-calls-virtual-background-content">
          {/* Video Preview */}
          <div className="cometchat-calls-virtual-background-preview">
            {renderVideoPreview()}
          </div>

          {/* Background Selection */}
          {renderBackgroundGrid()}
        </div>

        {/* Footer - Desktop only */}
        <div className="cometchat-calls-virtual-background-footer">
          <button
            className="cometchat-calls-virtual-background-button cometchat-calls-virtual-background-button--secondary"
            onClick={hideVirtualBackgroundDialog}
            aria-label={t('virtualBackground.buttons.cancel')}
            tabIndex={controlOptions.length + imageOptions.length + 2}>
            {t('virtualBackground.buttons.cancel')}
          </button>
          <button
            className="cometchat-calls-virtual-background-button cometchat-calls-virtual-background-button--primary"
            onClick={handleDone}
            aria-label={t('virtualBackground.buttons.done')}
            tabIndex={controlOptions.length + imageOptions.length + 3}
            disabled={!isDirty}>
            {t('virtualBackground.buttons.done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VirtualBackground;
